import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import StopIcon from '@mui/icons-material/Stop';
import {Box} from '@mui/material';
import {GridApi, GridColDef, GridRenderCellParams} from '@mui/x-data-grid';
import {t} from 'i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {useCallback} from 'react';
import {
  IDocumentRedaction,
  RedactionTags,
} from '../../../../../../api/types/document.type';
import {ISelectItem} from '../../../../../../shared/types';
import {isEmpty} from '../../../../../../utils/formatters';
import ArrIconButton from '../../../../../common/arr-icon-button/ArrIconButton';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {DocMediaTimePicker} from './DocMediaTimePicker';

export type DocRedactionPressType =
  | 'edit'
  | 'close'
  | 'play'
  | 'stop'
  | 'save'
  | 'remove';

interface DocumentRedactionListGridColumnsProps {
  apiRef: React.RefObject<GridApi | null>;
  prevSelectedRedactionTagId?: number;
  editRedaction?: IDocumentRedaction;
  handleActionPress?: (
    type: DocRedactionPressType,
    params: GridRenderCellParams,
    updatedRedaction?: Partial<IDocumentRedaction>
  ) => void;
  redactionTags: RedactionTags[];
  isSortable: boolean;
}

export const useDocRedactionsColumns = () => {
  const DocRedactionsColumns = useCallback(
    ({
      apiRef,
      prevSelectedRedactionTagId,
      editRedaction,
      handleActionPress,
      redactionTags,
      isSortable,
    }: DocumentRedactionListGridColumnsProps): GridColDef<IDocumentRedaction>[] => {
      const redactionTagArray: ISelectItem[] = redactionTags.map(
        (item: RedactionTags) => ({
          id: item.redactionID,
          name: item.name,
        })
      );

      const reductionTag =
        (redactionTagArray ?? []).find(
          (item: ISelectItem) =>
            (isEmpty(editRedaction) &&
              !isEmpty(prevSelectedRedactionTagId) &&
              item?.id === prevSelectedRedactionTagId) ||
            item?.name === editRedaction?.redactionLabels
        ) ??
        redactionTagArray?.[0] ??
        {};

      const updatedRedaction: Partial<IDocumentRedaction> = {
        ...(editRedaction ?? {}),
        ...{
          audioRedactionIDs: reductionTag?.id?.toString(),
          redactionLabels: reductionTag?.id?.toString(),
        },
      };

      return [
        {
          field: 'startTime',
          headerName: t('docRedactions.startTime'),
          width: 104,
          headerClassName: 'typo-text label3_size title-color',
          sortable: isSortable,
          renderCell: (params: GridRenderCellParams) => {
            const {documentAudioRedactionID: redactionID, startTime} =
              params.row;
            const {documentAudioRedactionID: editedRedactionID} =
              updatedRedaction ?? {};
            return redactionID === 0 || redactionID === editedRedactionID ? (
              <DocMediaTimePicker
                className="media-time-picker"
                initialValue={startTime}
                onChange={(value: string) => {
                  updatedRedaction['startTime'] = value;
                }}
              />
            ) : undefined;
          },
        },
        {
          field: 'endTime',
          headerName: t('docRedactions.endTime'),
          width: 104,
          headerClassName: 'typo-text label3_size title-color',
          sortable: isSortable,
          renderCell: (params: GridRenderCellParams) => {
            const {documentAudioRedactionID: redactionID, endTime} = params.row;
            const {documentAudioRedactionID: editedRedactionID} =
              updatedRedaction ?? {};
            return redactionID === 0 || redactionID === editedRedactionID ? (
              <DocMediaTimePicker
                className="media-time-picker"
                initialValue={endTime}
                onChange={(value: string) => {
                  updatedRedaction['endTime'] = value;
                }}
              />
            ) : undefined;
          },
        },
        {
          field: 'redactionLabels',
          headerName: t('docRedactions.redactionTags'),
          flex: 1,
          headerClassName: 'typo-text label3_size title-color',
          sortable: isSortable,
          renderCell: (params: GridRenderCellParams) => {
            const {documentAudioRedactionID: redactionID, redactionLabels} =
              params.row;
            const {
              documentAudioRedactionID: editedRedactionID,
              audioRedactionIDs: editedAudioRedactionIDs,
            } = updatedRedaction ?? {};

            return redactionID === 0 || redactionID === editedRedactionID ? (
              <ArrSingleSelectDropdown
                dropdownLabel={''}
                dataTestId={'asd-use-doc-redaction-tags-dropdown'}
                sizeVariant="small"
                dropdownItems={redactionTagArray}
                selectedValues={
                  redactionTagArray.find(
                    (item: ISelectItem) =>
                      (isEmpty(editRedaction) &&
                        !isEmpty(prevSelectedRedactionTagId) &&
                        item?.id === prevSelectedRedactionTagId) ||
                      item.id === editedAudioRedactionIDs ||
                      item.name === redactionLabels
                  ) ??
                  redactionTagArray?.[0] ??
                  null
                }
                onSelectionChange={(value: ISelectItem | null) => {
                  updatedRedaction['audioRedactionIDs'] = value?.id?.toString();
                  updatedRedaction['redactionLabels'] = value?.id?.toString();

                  if (editedRedactionID !== 0) {
                    apiRef.current?.updateRows([
                      {...editRedaction, redactionLabels: value?.name},
                    ]);
                  }
                }}
              />
            ) : undefined;
          },
        },
        {
          field: 'actions',
          headerName: t('docRedactions.actions'),
          width: 77,
          headerClassName: 'typo-text label3_size title-color',
          sortable: false,
          renderCell: (params: GridRenderCellParams) => {
            const {documentAudioRedactionID: redactionID} = params.row;
            const {documentAudioRedactionID: editedRedactionID} =
              updatedRedaction ?? {};

            const isAdd = redactionID === 0;
            const isEdit = redactionID === editedRedactionID;
            return (
              <Box className={'doc-redactions-actions'}>
                <ArrIconButton
                  icon={
                    isAdd || isEdit ? (
                      <SaveOutlinedIcon className="icon14" />
                    ) : (
                      <EditOutlinedIcon className="icon14" />
                    )
                  }
                  onClick={() =>
                    handleActionPress?.(
                      isAdd || isEdit ? 'save' : 'edit',
                      params,
                      {
                        ...updatedRedaction,
                      }
                    )
                  }
                  ariaLabel={t('assignment.dataGrid.showHideColumns')}
                  shape="none"
                  size="small"
                  testId={`save-edit-${params.id}`}
                />
                <ArrIconButton
                  icon={
                    isAdd || isEdit ? (
                      <CloseOutlinedIcon className="icon14" />
                    ) : (
                      <DeleteOutlinedIcon className="icon14" />
                    )
                  }
                  onClick={() =>
                    handleActionPress?.(
                      isAdd || isEdit ? 'close' : 'remove',
                      params
                    )
                  }
                  ariaLabel={t('assignment.dataGrid.showHideColumns')}
                  shape="none"
                  size="small"
                  testId={`close-remove-${params.id}`}
                />
              </Box>
            );
          },
        },
        {
          field: 'options',
          headerName: t('docRedactions.options'),
          width: 77,
          sortable: false,
          headerClassName: 'typo-text label3_size title-color options-header',
          renderCell: (params: GridRenderCellParams) => {
            return (
              <Box className={'doc-redactions-actions'}>
                <ArrIconButton
                  icon={<PlayArrowIcon className="icon14" />}
                  onClick={() => handleActionPress?.('play', params)}
                  ariaLabel={t('assignment.dataGrid.showHideColumns')}
                  shape="none"
                  size="small"
                  testId={`options-play-${params.id}`}
                />
                <ArrIconButton
                  icon={<StopIcon className="icon14" />}
                  onClick={() => handleActionPress?.('stop', params)}
                  ariaLabel={t('assignment.dataGrid.showHideColumns')}
                  shape="none"
                  size="small"
                  testId={`options-stop-${params.id}`}
                />
              </Box>
            );
          },
        },
      ];
    },
    []
  );

  return {
    DocRedactionsColumns,
  };
};
